/**
 * `<access-review>` — `SC-F825`, the effective access review report.
 *
 * It is mounted on the policy list, because the report is a read of exactly the
 * objects that list holds and because "who can reach what?" is the question a reader
 * of a policy list is trying to answer by scrolling it.
 *
 * # The design constraint is what this screen refuses to say
 *
 * A permission matrix is the most believable artifact in a governance product, and
 * that is the danger. Sluice Cloud runs no policy engine, so this screen never prints
 * a verdict: a cell carries a POSTURE — a statement about which authored objects can
 * reach the pair — and where an object's effect depends on something only the gateway
 * can evaluate the cell says INDETERMINATE rather than picking.
 *
 * Three things are therefore non-negotiable in this template:
 *
 *  1. **The basis paragraph and the five limits are always rendered**, verbatim from
 *     the API, above the matrix and not behind a disclosure. A matrix read without
 *     them is the artifact this feature exists to replace.
 *  2. **A shadow object is visibly a shadow.** An `Audit`-mode deny records what it
 *     would have applied and lets the query run; showing it as protection would be
 *     the most dangerous possible misreading of this screen, so it is badged and
 *     excluded from the posture that colours the cell.
 *  3. **Posture is never colour alone.** Every cell prints its posture as a word.
 *     The tone is a second channel, not the channel.
 *
 * All four states: loading, empty (no bindings, or nothing matching the narrowing),
 * error (the `apierr` code AND the request id), populated.
 *
 * Stories: `SC-S519`, `SC-S804` (the retrospective half this is the counterpart to).
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnBadgeVariant } from "../components/index.js";

import {
  accessReviewCSVHref,
  getAccessReview,
  type AccessReview,
  type ReviewCell,
  type ReviewPosture,
  type ReviewPrincipal,
} from "../api/policies.js";
import { ApiError, parseError } from "../api/errors.js";

/** How each posture reads. The WORD is the encoding and the tone is a second
 * channel — a matrix whose meaning lived in five colours would be unreadable to a
 * third of readers and invisible under forced colours. */
const POSTURE: Record<ReviewPosture, { label: string; variant: BnBadgeVariant; hint: string }> = {
  "denied-by-default": {
    label: "Denied by default",
    variant: "neutral",
    hint: "No enforcing allow's selector can reach this pair, so Sluice's default-deny refuses it.",
  },
  "denied-explicitly": {
    label: "Denied",
    variant: "success",
    hint: "An enforcing deny reaches this pair unconditionally. Deny wins over allow at any priority.",
  },
  "allow-authored": {
    label: "Allow authored",
    variant: "warning",
    hint:
      "An enforcing allow can reach this pair. That means “not denied by access policy” — " +
      "not that every query succeeds.",
  },
  conditional: {
    label: "Conditional",
    variant: "info",
    hint:
      "The matching allow or deny depends on something evaluated per request — a CEL condition, a " +
      "claim comparison, an SQL action narrowing — which this report does not evaluate.",
  },
  indeterminate: {
    label: "Indeterminate",
    variant: "error",
    hint:
      "The configuration could not be read for this pair: an object this build cannot decode, or a " +
      "classification tag nothing in this generation defines.",
  },
};

/** The posture order for the summary strip: strongest statement first. */
const POSTURE_ORDER: ReviewPosture[] = [
  "denied-by-default",
  "denied-explicitly",
  "allow-authored",
  "conditional",
  "indeterminate",
];

@customElement("access-review")
export class AccessReviewPanel extends LitElement {
  @property() instanceId = "";

  @state() private report: AccessReview | null = null;
  @state() private loading = false;
  @state() private opened = false;
  @state() private error: ApiError | null = null;
  @state() private catalog = "";
  @state() private binding = "";
  /** Which principal's row is expanded. A matrix of every pair at once is a wall; a
   * reader works one identity at a time, which is also how an auditor asks. */
  @state() private expanded = -1;

  private seq = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .lede,
    .meta {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .lede {
      margin-bottom: var(--bino-space-md);
    }
    .basis {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text);
      max-width: 82ch;
    }
    ol.limits {
      margin: 0 0 var(--bino-space-lg);
      padding-left: 1.4em;
      display: grid;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
      flex: 1 1 12rem;
    }
    .stamp {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm) var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .summary {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
    }
    .count {
      font-variant-numeric: tabular-nums;
    }
    .rows {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .row {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
    }
    .row-head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm) var(--bino-space-md);
      width: 100%;
      padding: var(--bino-space-md);
      background: none;
      border: 0;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
      border-radius: var(--bino-radius);
    }
    .row-head:hover {
      background: var(--bino-surface-hover);
    }
    .row-head:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: -2px;
    }
    .identity {
      font-family: var(--bino-font-mono);
      flex: 1 1 16rem;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .sub {
      display: block;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
    }
    .cells {
      border-top: 1px solid var(--bino-border-light);
      padding: var(--bino-space-md);
      display: grid;
      gap: var(--bino-space-md);
    }
    .cell {
      display: grid;
      gap: var(--bino-space-xs);
      padding-bottom: var(--bino-space-md);
      border-bottom: 1px solid var(--bino-border-light);
    }
    .cell:last-child {
      padding-bottom: 0;
      border-bottom: 0;
    }
    .cell-head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .table-name {
      font-family: var(--bino-font-mono);
      overflow-wrap: anywhere;
    }
    .hint,
    .note {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    ul.matches,
    ul.notes {
      margin: 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
    }
    ul.matches code {
      font-family: var(--bino-font-mono);
    }
    .why {
      display: block;
      color: var(--bino-text-muted);
      font-size: var(--bino-font-size-xs);
      max-width: 82ch;
    }
    .masks {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs) var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
    }
    .mask {
      font-family: var(--bino-font-mono);
    }
    .error-message {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-error);
    }
  `;

  private async load() {
    const mine = ++this.seq;
    this.loading = true;
    this.opened = true;
    try {
      const res = await getAccessReview(this.instanceId, {
        catalog: this.catalog || undefined,
        binding: this.binding || undefined,
      });
      if (mine !== this.seq) return;
      this.report = res;
      this.error = null;
      this.expanded = res.principals.length === 1 ? 0 : -1;
    } catch (e) {
      if (mine !== this.seq) return;
      this.error = parseError(e);
      this.report = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  /** cellsFor is one principal's row of the matrix. */
  private cellsFor(report: AccessReview, index: number): ReviewCell[] {
    return report.cells.filter((c) => c.principal === index);
  }

  private renderIdentity(p: ReviewPrincipal) {
    if (p.kind === "jwt-issuer") {
      return html`<span class="identity"
        >${p.issuer || "(no issuer)"}
        <span class="sub"
          >${p.binding} · every subject this issuer signs a token for</span
        ></span
      >`;
    }
    return html`<span class="identity"
      >${p.id}
      <span class="sub"
        >${p.binding} · API key${p.groups && p.groups.length > 0
          ? ` · groups ${p.groups.join(", ")}`
          : ""}</span
      ></span
    >`;
  }

  /** A row's posture roll-up: the worst statement in it, so a reader scanning
   * identities can see which one to open. "Worst" here is the widest reach —
   * indeterminate first, then allow, because those are the two that need a human. */
  private worstOf(cells: ReviewCell[]): ReviewPosture {
    for (const p of ["indeterminate", "allow-authored", "conditional", "denied-explicitly"] as const) {
      if (cells.some((c) => c.posture === p)) return p;
    }
    return "denied-by-default";
  }

  private renderCell(cell: ReviewCell) {
    const meta = POSTURE[cell.posture];
    const shadows = (cell.matches ?? []).filter((m) => m.shadow);
    const live = (cell.matches ?? []).filter((m) => !m.shadow);
    return html`<div class="cell">
      <div class="cell-head">
        <span class="table-name">${cell.table}</span>
        <bn-badge .variant=${meta.variant}>${meta.label}</bn-badge>
        ${cell.templated ? html`<bn-badge variant="info">Templated filter</bn-badge>` : nothing}
      </div>
      <p class="hint">${meta.hint}</p>
      ${live.length > 0
        ? html`<ul class="matches">
            ${live.map(
              (m) => html`<li>
                <code>${m.kind}/${m.name}</code> — ${m.effect}, priority ${m.priority}
                ${m.certainty !== "static"
                  ? html`<bn-badge variant="info">${m.certainty}</bn-badge>`
                  : nothing}
                ${m.why ? html`<span class="why">${m.why}</span>` : nothing}
              </li>`,
            )}
          </ul>`
        : nothing}
      ${shadows.length > 0
        ? html`<ul class="matches">
            ${shadows.map(
              (m) => html`<li>
                <bn-badge variant="warning">Shadow</bn-badge>
                <code>${m.kind}/${m.name}</code> — ${m.effect} in ${m.enforcement} mode, so it
                records what it would have done and changes nothing.
              </li>`,
            )}
          </ul>`
        : nothing}
      ${cell.masked && cell.masked.length > 0
        ? html`<div class="masks">
            ${cell.masked.map(
              (m) => html`<span
                ><span class="mask">${m.column}</span> — ${m.mask}, class ${m.class_label}${m.shadow
                  ? " (shadow: NOT masked)"
                  : ""}</span
              >`,
            )}
          </div>`
        : nothing}
      ${cell.row_filters && cell.row_filters.length > 0
        ? html`<p class="note">Row filters: ${cell.row_filters.join(", ")}.</p>`
        : nothing}
      ${cell.notes && cell.notes.length > 0
        ? html`<ul class="notes">
            ${cell.notes.map((n) => html`<li class="why">${n}</li>`)}
          </ul>`
        : nothing}
    </div>`;
  }

  private renderRows(report: AccessReview): TemplateResult {
    return html`<div class="rows">
      ${report.principals.map((p, i) => {
        const cells = this.cellsFor(report, i);
        const worst = POSTURE[this.worstOf(cells)];
        const open = this.expanded === i;
        return html`<div class="row">
          <button
            class="row-head"
            aria-expanded=${open ? "true" : "false"}
            @click=${() => (this.expanded = open ? -1 : i)}
          >
            ${this.renderIdentity(p)}
            <bn-badge .variant=${worst.variant}>${worst.label}</bn-badge>
            <span class="count">${cells.length} table${cells.length === 1 ? "" : "s"}</span>
            ${p.enumerable ? nothing : html`<bn-badge variant="neutral">Not enumerable</bn-badge>`}
            <span aria-hidden="true">${open ? "▾" : "▸"}</span>
          </button>
          ${open
            ? html`<div class="cells">
                ${p.note ? html`<p class="note">${p.note}</p>` : nothing}
                ${cells.map((c) => this.renderCell(c))}
              </div>`
            : nothing}
        </div>`;
      })}
    </div>`;
  }

  private renderError(err: ApiError) {
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote.
          </p>`}
      <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
    </div>`;
  }

  override render() {
    const report = this.report;
    return html`<bn-card heading="Effective access review">
      <p class="lede">
        Who can reach what, across this instance, according to the configuration you have authored.
        It is the forward-looking half of the evidence story: the audit trail says what was done, the
        chain says the trail was not edited, and this says what the controls permit.
      </p>

      <div class="toolbar">
        <bn-field label="Catalog" hint="Narrow the columns. Exact name, not a pattern.">
          <bn-input
            .value=${this.catalog}
            placeholder="warehouse"
            @input=${(e: CustomEvent<{ value: string }>) => (this.catalog = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Binding" hint="Narrow the rows to one SubjectBinding.">
          <bn-input
            .value=${this.binding}
            @input=${(e: CustomEvent<{ value: string }>) => (this.binding = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button variant="primary" ?loading=${this.loading} @click=${() => void this.load()}>
          ${this.opened ? "Rebuild the report" : "Build the report"}
        </bn-button>
        ${report
          ? html`<bn-button
              @click=${() =>
                window.open(
                  accessReviewCSVHref(this.instanceId, {
                    catalog: this.catalog || undefined,
                    binding: this.binding || undefined,
                  }),
                  "_blank",
                  "noopener",
                )}
            >
              Download CSV
            </bn-button>`
          : nothing}
      </div>

      ${this.error
        ? this.renderError(this.error)
        : this.loading && !report
          ? html`<bn-spinner block size="28" label="Assembling the report"></bn-spinner>`
          : !report
            ? html`<bn-empty-state
                icon="⚖"
                heading="Not built yet"
                message="The report is assembled on demand from this instance's authored policy set and its schema cache. It is not a live view — it is stamped with one generation, which is the correct semantics for an audit artifact and the reason it is a button rather than a page load."
              ></bn-empty-state>`
            : html`
                <!-- Always rendered, never behind a disclosure: a permission matrix
                     read without its basis is the artifact this feature replaces. -->
                <p class="basis">${report.basis}</p>
                <ol class="limits">
                  ${report.limits.map((l) => html`<li>${l}</li>`)}
                </ol>

                <div class="stamp">
                  <span
                    >Generation <strong>${report.generation}</strong>
                    (${report.generation_state})</span
                  >
                  <span>Assembled ${when(report.generated_at)}</span>
                  <span
                    >Schema cache
                    ${report.schema.fetched_at
                      ? html`read ${when(report.schema.fetched_at)}`
                      : html`empty`}${report.schema.fresh
                      ? nothing
                      : html` <bn-badge variant="warning">stale</bn-badge>`}${report.schema
                      .truncated
                      ? html` <bn-badge variant="warning">incomplete</bn-badge>`
                      : nothing}</span
                  >
                </div>

                ${report.warnings && report.warnings.length > 0
                  ? html`<bn-panel eyebrow="Conditions that narrow this report">
                      <ul class="notes">
                        ${report.warnings.map((w) => html`<li>${w}</li>`)}
                      </ul>
                    </bn-panel>`
                  : nothing}

                <div class="summary">
                  ${POSTURE_ORDER.filter((p) => (report.summary[p] ?? 0) > 0).map(
                    (p) => html`<bn-badge .variant=${POSTURE[p].variant}
                      >${POSTURE[p].label}: ${report.summary[p]}</bn-badge
                    >`,
                  )}
                </div>

                ${report.principals.length === 0
                  ? html`<bn-empty-state
                      icon="∅"
                      heading="No identity to review"
                      message="No SubjectBinding in this generation resolves an identity, so there is nobody for a policy to grant or refuse. Author a binding first — until then no credential authenticates against this instance at all."
                    ></bn-empty-state>`
                  : report.tables.length === 0
                    ? html`<bn-empty-state
                        icon="⌕"
                        heading="No table to review"
                        message="Either the narrowing matched nothing, or the schema cache holds no table for this instance. An empty cache is not an empty database — refresh it from the datasources screen."
                      ></bn-empty-state>`
                    : this.renderRows(report)}
              `}
    </bn-card>`;
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date" if the server sends something this bundle cannot
 * parse. */
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "access-review": AccessReviewPanel;
  }
}
